import React, { Fragment, useContext, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Badge } from 'reactstrap';
import { PlusCircle, Edit, Trash2, Image as ImageIcon, Layers, X } from 'react-feather';
import { Breadcrumbs } from '../../../../AbstractElements';
import CustomizerContext from '../../../../_helper/Customizer';
import { fetchLoadingBannerQLT, fetchUploadBannerQLT, fetchEditBannerQLT, fetchDeleteBannerQLT } from '../../../../api/bannerQLT';
import { toast } from 'react-toastify';

const BannerQLTManagement = () => {
  const { layoutURL } = useContext(CustomizerContext);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentBanner, setCurrentBanner] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  // Load banners khi component mount
  useEffect(() => {
    loadBanners();
  }, []);

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Hàm load danh sách banner QLT
  const loadBanners = async () => {
    setLoading(true);
    try {
      const data = await fetchLoadingBannerQLT();
      setBanners(data);
      toast.success('✅ Tải danh sách banner QLT thành công!');
    } catch (error) {
      toast.error('❌ Lỗi khi tải banner: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý chọn file
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('⚠️ Vui lòng chọn file ảnh!');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('⚠️ File ảnh quá lớn! Vui lòng chọn file nhỏ hơn 10MB.');
        return;
      }

      setSelectedFile(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    }
  };

  // Mở modal thêm mới
  const handleAddNew = () => {
    setEditMode(false);
    setCurrentBanner(null);
    setSelectedFile(null);
    setPreviewUrl('');
    setModalOpen(true);
  };

  // Mở modal chỉnh sửa
  const handleEdit = (banner) => {
    setEditMode(true);
    setCurrentBanner(banner);
    setPreviewUrl(banner.url);
    setSelectedFile(null);
    setModalOpen(true);
  };

  // Xử lý upload/edit banner
  const handleSubmit = async () => {
    if (!selectedFile && !editMode) {
      toast.warning('⚠️ Vui lòng chọn file ảnh!');
      return;
    }

    setLoading(true);
    try {
      if (editMode && currentBanner) {
        // Edit banner
        const formData = new FormData();
        if (selectedFile) {
          formData.append('url', selectedFile);
        }
        await fetchEditBannerQLT(currentBanner._id, formData);
        toast.success('✅ Cập nhật banner QLT thành công!');
      } else {
        // Upload new banner
        await fetchUploadBannerQLT(selectedFile);
        toast.success('✅ Thêm banner QLT mới thành công!');
      }
      
      setModalOpen(false);
      loadBanners();
    } catch (error) {
      toast.error('❌ Lỗi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý xóa banner
  const handleDelete = async (id) => {
    if (window.confirm('⚠️ Bạn có chắc chắn muốn xóa banner này?')) {
      setLoading(true);
      try {
        await fetchDeleteBannerQLT(id);
        toast.success('✅ Xóa banner QLT thành công!');
        loadBanners();
      } catch (error) {
        toast.error('❌ Lỗi khi xóa banner: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Banner QLT" title="Banner QLT Management" mainTitle="Quản lý Banner QLT" />
      <Container fluid={true}>
        <Row>
          <Col md="12">
            {/* Info Banner */}
            <Card className="bg-primary text-white mb-4 border-0 shadow-sm">
              <CardBody className="p-4">
                <div className="d-flex align-items-center">
                  <div className="bg-white bg-opacity-25 rounded-circle p-3 me-3">
                    <Layers size={32} />
                  </div>
                  <div>
                    <h4 className="text-white mb-1">Banner QLT (Quản Lý Tài sản)</h4>
                    <p className="mb-0 opacity-75">
                      Quản lý banner hiển thị trên module Quản lý tài sản
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Main Content Card */}
            <Card className="shadow-sm border-0">
              <CardBody className="p-4">
                {/* Header Section */}
                <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                  <div>
                    <h4 className="mb-1">
                      <ImageIcon size={24} className="me-2 text-primary" />
                      Danh sách Banner QLT
                    </h4>
                    <p className="text-muted mb-0 small">
                      {banners.length} banner đang được quản lý
                    </p>
                  </div>
                  <Button color="primary" onClick={handleAddNew} className="shadow-sm">
                    <PlusCircle size={16} className="me-2" />
                    Thêm Banner Mới
                  </Button>
                </div>

                {/* Loading State */}
                {loading && (
                  <div className="text-center my-5 py-5">
                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-muted">Đang tải dữ liệu...</h5>
                    <p className="text-muted small">Vui lòng chờ trong giây lát</p>
                  </div>
                )}

                {/* Empty State */}
                {!loading && banners.length === 0 && (
                  <div className="text-center my-5 py-5">
                    <div className="mb-4">
                      <ImageIcon size={100} className="text-muted opacity-50" />
                    </div>
                    <h4 className="text-muted mb-2">Chưa có banner QLT nào</h4>
                    <p className="text-muted mb-4">
                      Bắt đầu bằng cách thêm banner đầu tiên cho module Quản lý tài sản
                    </p>
                    <Button color="primary" size="lg" onClick={handleAddNew}>
                      <PlusCircle size={18} className="me-2" />
                      Thêm Banner Đầu Tiên
                    </Button>
                  </div>
                )}

                {/* Banners Grid */}
                {!loading && banners.length > 0 && (
                  <Row>
                    {banners.map((banner, index) => (
                      <Col xl="4" lg="6" md="6" sm="12" key={banner._id || index} className="mb-4">
                        <Card className="shadow-sm border h-100 hover-lift">
                          {/* Image Container */}
                          <div className="position-relative overflow-hidden" style={{ paddingTop: '56.25%' }}>
                            <img
                              src={banner.url}
                              alt={`Banner QLT ${index + 1}`}
                              className="position-absolute top-0 start-0 w-100 h-100 hover-zoom"
                              style={{ objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/800x450?text=Banner+QLT';
                              }}
                            />
                            {/* Index Badge */}
                            <div className="position-absolute top-0 start-0 m-3">
                              <Badge color="dark" className="px-3 py-2">
                                #{index + 1}
                              </Badge>
                            </div>
                          </div>

                          {/* Card Body */}
                          <CardBody className="bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="flex-grow-1">
                                <p className="mb-1 small text-muted">
                                  <strong>ID:</strong> {banner._id?.slice(-8)}
                                </p>
                                <p className="mb-0 small text-muted">
                                  <strong>Banner QLT</strong> - Module Quản lý tài sản
                                </p>
                              </div>
                              <div className="btn-group">
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => handleEdit(banner)}
                                  title="Chỉnh sửa"
                                >
                                  <Edit size={14} />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDelete(banner._id)}
                                  title="Xóa"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}

                {/* Statistics Footer */}
                {!loading && banners.length > 0 && (
                  <div className="mt-4 pt-4 border-top">
                    <Row className="text-center">
                      <Col md="4">
                        <div className="p-3">
                          <h2 className="text-primary mb-1">{banners.length}</h2>
                          <p className="text-muted mb-0 small">Tổng số banner</p>
                        </div>
                      </Col>
                      <Col md="4">
                        <div className="p-3">
                          <h2 className="text-success mb-1">
                            <Layers size={32} />
                          </h2>
                          <p className="text-muted mb-0 small">Module QLT</p>
                        </div>
                      </Col>
                      <Col md="4">
                        <div className="p-3">
                          <h2 className="text-info mb-1">16:9</h2>
                          <p className="text-muted mb-0 small">Tỷ lệ khuyến nghị</p>
                        </div>
                      </Col>
                    </Row>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal Upload/Edit Banner */}
        <Modal isOpen={modalOpen} toggle={handleCloseModal} size="lg" centered>
          <ModalHeader toggle={handleCloseModal} className="bg-light">
            <div className="d-flex align-items-center">
              <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                {editMode ? <Edit size={20} className="text-primary" /> : <PlusCircle size={20} className="text-primary" />}
              </div>
              <div>
                <h5 className="mb-0">
                  {editMode ? '✏️ Chỉnh sửa Banner QLT' : '➕ Thêm Banner QLT Mới'}
                </h5>
                <small className="text-muted">
                  {editMode ? 'Cập nhật thông tin banner' : 'Upload banner cho module Quản lý tài sản'}
                </small>
              </div>
            </div>
          </ModalHeader>
          <ModalBody className="p-4">
            <FormGroup>
              <Label for="bannerFile" className="fw-bold fs-6 mb-3">
                <ImageIcon size={18} className="me-2 text-primary" />
                Chọn ảnh Banner <span className="text-danger">*</span>
              </Label>
              <Input
                id="bannerFile"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="form-control-lg"
              />
              <div className="mt-3 p-3 bg-light rounded">
                <small className="text-muted d-block mb-2">
                  <strong>📌 Yêu cầu:</strong>
                </small>
                <ul className="small text-muted mb-0 ps-3">
                  <li>Định dạng: JPG, PNG, GIF, WebP</li>
                  <li>Kích thước tối đa: 10MB</li>
                  <li>Tỷ lệ khuyến nghị: 16:9 (1920x1080px hoặc tương tự)</li>
                  <li>Nội dung phù hợp với module Quản lý tài sản</li>
                </ul>
              </div>
            </FormGroup>

            {/* Preview Section */}
            {previewUrl && (
              <div className="mt-4">
                <Label className="fw-bold fs-6 mb-3">
                  🖼️ Xem trước Banner:
                </Label>
                <div className="border rounded overflow-hidden shadow-sm">
                  <div className="position-relative" style={{ paddingTop: '56.25%', backgroundColor: '#f8f9fa' }}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="position-absolute top-0 start-0 w-100 h-100"
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                  <div className="bg-light p-3 text-center border-top">
                    <Badge color="success" className="px-3 py-2">
                      ✓ Banner QLT Preview
                    </Badge>
                  </div>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter className="bg-light">
            <Button color="secondary" onClick={handleCloseModal} disabled={loading}>
              <X size={16} className="me-2" />
              Hủy bỏ
            </Button>
            <Button 
              color="primary" 
              onClick={handleSubmit} 
              disabled={loading || (!selectedFile && !editMode)}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Đang xử lý...
                </>
              ) : (
                <>
                  {editMode ? <Edit size={16} className="me-2" /> : <PlusCircle size={16} className="me-2" />}
                  {editMode ? 'Cập nhật Banner' : 'Thêm Banner'}
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>

      {/* Custom CSS for hover effects */}
      <style>{`
        .hover-lift {
          transition: all 0.3s ease;
        }
        .hover-lift:hover {
          transform: translateY(-5px);
          box-shadow: 0 1rem 3rem rgba(0,0,0,.175) !important;
        }
        .hover-zoom {
          transition: transform 0.3s ease;
        }
        .hover-lift:hover .hover-zoom {
          transform: scale(1.05);
        }
      `}</style>
    </Fragment>
  );
};

export default BannerQLTManagement;

